import {
  ClientWorkFlowStatus,
  FrequencyAndScheduleStatus,
  IAdditionalData,
  IBulkBatchCreate,
  IBulkBatchUpdate,
  IBulkPayableBatchCreate,
  IBulkPayableBatchUpdate,
  ICalculate1099Request,
  IClientInvoice,
  IClientInvoiceCreateRequest,
  IClientInvoiceTemplate,
  IClientInvoiceTemplateUpdateRequest,
  IClientInvoiceUpdateRequest,
  ICollaboratorCreateRequest,
  ICollaboratorSchema,
  ICollaboratorUpdateRequest,
  IInvoice,
  IInvoiceCollaboratorCreateRequest,
  IInvoiceTemplate,
  IMemberClient,
  IMemberClientCreateRequest,
  IMemberClientUpdateRequest,
  InvoiceStatus,
  IPayableSchema,
  IPayRequest,
  IPayrollSettings,
  IPayrollSettingsUpdate,
  IScheduleDateUpdate,
  IServiceUpdateRequest,
  MemberWorkFlowStatus,
  ScheduleStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  CardStatusUpdate,
  ICardCodeRequest,
  ICardCreateRequest,
  ICardTokenRequest,
  ICardUpdateRequest
} from "@wingspanhq/payments/dist/interfaces/api/card";
import {
  Currency,
  UserAccountType
} from "@wingspanhq/users/dist/lib/interfaces";
import { AxiosError } from "axios";
import { isEqual } from "lodash";
import {
  queryCache,
  QueryConfig,
  ReactQueryMutationsConfig
} from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { addNotification } from "../../components/Notification/Notification";
import { CollaboratorValues } from "../../Invoices/components/InvoicesForm/CollaboratorsSection";
import { InvoicesFormValues } from "../../Invoices/components/InvoicesForm/InvoicesForm";
import {
  PayableFormValues,
  SendPayableInvoiceAction
} from "../../Invoices/components/PayableForm/PayableForm";
import {
  useDownloadPdf,
  useGetInvoice,
  useGetInvoiceTemplate,
  useGetMemberClient
} from "../../Invoices/utils";
import {
  attachmentsFormDataToCreateRequest,
  attachmentsFormDataToUpdateRequest,
  convertToLineItemsCreateRequest,
  convertToLineItemsUpdateRequest,
  convertToLineItemsUpdateRequest2,
  generateDueDate,
  generateFrequencyConfig,
  generateLateFeeConfig,
  generateSendDate,
  paymentMethodsFormDataToCreateRequest,
  paymentMethodsFormDataToUpdateRequest,
  useInvoiceFormGoBack
} from "../../Invoices/utils/invoicesFormUtils";

import { paymentsService } from "../../services/payments";
import { isToday } from "../../utils/dates";
import { getChangedData } from "../../utils/getChangedData";
import { openInNewTab } from "../../utils/openInNewTab";
import { useWSAnalytics } from "../../utils/WSAnalytics";
import { QUERY_BOOKKEEPING_BANKING_ACCOUNT } from "../bookkeeping/keys";
import { useWSMutation } from "../helpers";
import { useUserId } from "../hooks/helpers";
import { useMemberProfile, useUserProfile } from "../users/queries";
import {
  QUERY_ALL_COLLABORATOR_GROUPS,
  QUERY_CARD,
  QUERY_CARD_LIST,
  QUERY_CLIENT_INVOICE,
  QUERY_CLIENT_INVOICE_TEMPLATE,
  QUERY_COLLABORATOR,
  QUERY_COLLABORATORS,
  QUERY_COLLABORATOR_GROUP,
  QUERY_COLLABORATOR_GROUPS,
  QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_DATA,
  QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_ITEM_DATA,
  QUERY_INVOICE,
  QUERY_INVOICES,
  QUERY_INVOICE_TEMPLATE,
  QUERY_INVOICE_TEMPLATES,
  QUERY_MEMBER_CLIENT,
  QUERY_MEMBER_CLIENTS,
  QUERY_PAYABLE,
  QUERY_PAYABLES,
  QUERY_PAYOUT_SETTINGS,
  QUERY_CLIENT_DEDUCTIONS,
  QUERY_CLIENT_DEDUCTION,
  QUERY_COLLABORATOR_DEDUCTIONS,
  QUERY_COLLABORATOR_DEDUCTION,
  QUERY_BULK_PAYABLE_BATCH,
  QUERY_PAYROLL_SETTINGS,
  QUERY_BULK_CALCULATE_1099_BATCH,
  QUERY_BULK_COLLABORATOR_BATCH
} from "./keys";

import {
  ICollaboratorGroupCreateRequest,
  ICollaboratorGroupUpdateRequest
} from "@wingspanhq/payments/dist/interfaces/api/collaboratorGroup";
import {
  IDeductionCreateRequest,
  IDeductionUpdateRequest
} from "@wingspanhq/payments/dist/interfaces/api/deductions";
import {
  useCollaboratorsQuery,
  useMemberClientsQuery,
  usePaymentsStatusQuery
} from "./queries";
import { getClients, getVisibleCollaborators } from "./selectors";
import { IBulkCalculation1099Batch } from "@wingspanhq/payments/dist/interfaces/bulkCalculation1099";

const findCreatedInvoice = async (
  invoiceTemplateId: string
): Promise<IInvoice | undefined> => {
  try {
    return (await paymentsService.invoice.list()).find(
      invoice => invoice.invoiceTemplateId === invoiceTemplateId
    );
  } catch (error) {
    console.error("Failed to fetch invoices: ", error);
  }
};

export const useSendInvoiceTemplate = () => {
  const history = useHistory();

  return useWSMutation<
    any,
    AxiosError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        status: FrequencyAndScheduleStatus.Active
      });

      const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

      if (createdInvoice) {
        history.push(
          `/member/invoices/${createdInvoice.invoiceId}/send-success`
        );
      } else {
        history.push(
          `/member/invoices/template/${invoiceTemplateId}/send-success`
        );
      }
    },
    {
      throwOnError: true,
      dependencies: [QUERY_MEMBER_CLIENTS, QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useSendNowInvoiceTemplate = () => {
  const history = useHistory();

  return useWSMutation<
    any,
    AxiosError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        scheduleDates: [{ date: new Date(), status: ScheduleStatus.Pending }],
        status: FrequencyAndScheduleStatus.Active
      });

      const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

      if (createdInvoice) {
        history.push(
          `/member/invoices/${createdInvoice.invoiceId}/send-success`
        );
      } else {
        history.push(
          `/member/invoices/template/${invoiceTemplateId}/send-success`
        );
      }
    },
    {
      throwOnError: true,
      dependencies: [QUERY_MEMBER_CLIENTS, QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useSendInvoiceReminder = () => {
  const getInvoice = useGetInvoice();
  const getMemberClient = useGetMemberClient();

  return useWSMutation<
    any,
    AxiosError,
    {
      invoiceId: string;
    }
  >(async ({ invoiceId }) => {
    await paymentsService.invoice.sendReminder(invoiceId);

    const invoice = getInvoice(invoiceId);
    const memberClient = invoice
      ? getMemberClient(invoice.memberClientId as string)
      : undefined;

    const toText = memberClient?.emailCC?.length
      ? `${memberClient.name} and ${memberClient.emailCC.join(", ")}`
      : memberClient?.name;

    const text = `Invoice reminder was sent to ${toText}`;

    addNotification({ text });
  });
};

export const useAcceptInvoice = () => {
  return useWSMutation<
    any,
    AxiosError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      await paymentsService.invoice.update(invoiceId, {
        member: {
          workflowStatus: MemberWorkFlowStatus.Accepted
        }
      });
    },
    {
      onSuccess: (_, { invoiceId }) => {
        queryCache.invalidateQueries(QUERY_INVOICES);
        queryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
      }
    }
  );
};

export const useDisputeInvoice = () => {
  return useWSMutation<
    any,
    AxiosError,
    {
      invoiceId: string;
      disputeComment?: string;
    }
  >(
    async ({ invoiceId, disputeComment }) => {
      await paymentsService.invoice.update(invoiceId, {
        member: {
          workflowStatus: MemberWorkFlowStatus.Disputed,
          comment: disputeComment
        }
      });
    },
    {
      onSuccess: (_, { invoiceId }) => {
        queryCache.invalidateQueries(QUERY_INVOICES);
        queryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
      }
    }
  );
};

export const useMarkInvoiceAsPaid = () => {
  return useWSMutation<
    IInvoice,
    AxiosError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      return await paymentsService.invoice.update(invoiceId, {
        status: InvoiceStatus.Paid
      });
    },
    {
      dependencies: [QUERY_INVOICES]
    }
  );
};

export const useCancelInvoice = () => {
  return useWSMutation<
    IInvoice,
    AxiosError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      return await paymentsService.invoice.update(invoiceId, {
        status: InvoiceStatus.Cancelled
      });
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICES]
    }
  );
};

export const useCancelInvoiceTemplate = () => {
  return useWSMutation<
    IInvoiceTemplate,
    AxiosError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      return await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        status: FrequencyAndScheduleStatus.Expired,
        frequency: {
          endDate: new Date()
        }
      });
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useDeleteInvoice = () => {
  return useWSMutation<
    IInvoice,
    AxiosError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      return await paymentsService.invoice.delete(invoiceId);
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICES]
    }
  );
};

export const useDeleteInvoiceTemplate = () => {
  return useWSMutation<
    IInvoiceTemplate,
    AxiosError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      return await paymentsService.invoiceTemplate.delete(invoiceTemplateId);
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const createCollaboratorsRequest = async (
  data: CollaboratorValues[],
  collaborators: ICollaboratorSchema[],
  userId: string,
  defaultCurrency?: Currency
) => {
  const collaboratorsRequest: (IInvoiceCollaboratorCreateRequest | null)[] = [];

  const collaboratorPromises = (data || []).map(async collaboratorData => {
    if (collaboratorData.remove) {
      collaboratorsRequest.push(null);
    } else {
      let collaboratorId;
      const matchingCollaborator = collaborators.find(
        memberClient =>
          memberClient.member.user.email === collaboratorData.email
      );

      if (matchingCollaborator) {
        collaboratorId = matchingCollaborator.collaboratorId;
      } else {
        const createdMemberClient = await paymentsService.collaborator.create({
          clientId: userId,
          memberEmail: collaboratorData.email,
          labels: collaboratorData.labels,
          ...(collaboratorData.qboVendorId !== ""
            ? {
                integration: {
                  quickbooks: {
                    vendorId: collaboratorData.qboVendorId,
                    expenseAccountId: collaboratorData.qboExpenseAccountId
                  }
                }
              }
            : {})
        });
        collaboratorId = createdMemberClient.collaboratorId;
      }

      collaboratorsRequest.push({
        memberClientId: collaboratorId,
        amount: Number(collaboratorData.amount),
        currency: defaultCurrency || Currency.USD,
        description: collaboratorData.description
      });
    }
  });

  await Promise.all(collaboratorPromises);

  return collaboratorsRequest;
};

export const useSaveInvoice = () => {
  const userId = useUserId();
  const memberClientsQuery = useMemberClientsQuery();
  const collaboratorsQuery = useCollaboratorsQuery();
  const getInvoice = useGetInvoice();
  const getInvoiceTemplate = useGetInvoiceTemplate();
  const userQuery = useUserProfile(userId);
  const [sendInvoice] = useSendInvoice();
  const goBack = useInvoiceFormGoBack();
  const paymentsStatusQuery = usePaymentsStatusQuery();

  return useWSMutation<
    {
      savedInvoice?: IInvoice;
      savedInvoiceTemplate?: IInvoiceTemplate;
    },
    AxiosError,
    {
      invoiceId?: string;
      invoiceTemplateId?: string;
      data: InvoicesFormValues;
      send?: boolean;
    }
  >(
    async ({ invoiceId, invoiceTemplateId, data, send }) => {
      let invoice: IInvoice | undefined;
      let invoiceTemplate: IInvoiceTemplate | undefined;
      let savedInvoice: IInvoice | undefined;
      let savedInvoiceTemplate: IInvoiceTemplate | undefined;

      const memberClients = getClients(memberClientsQuery.data || []);
      const collaborators = getVisibleCollaborators(
        collaboratorsQuery.data || []
      );

      if (invoiceId) {
        invoice = getInvoice(invoiceId);
      }

      if (invoiceTemplateId) {
        invoiceTemplate = getInvoiceTemplate(invoiceTemplateId);
      }

      // Client

      let memberClientId: string | undefined;

      // If it's is invoice or invoiceTemplate edit – don't do any changes to memberClient, just use memberClientId
      if (invoice) {
        memberClientId = invoice.memberClientId as string;
      } else if (invoiceTemplate) {
        memberClientId = invoiceTemplate.invoiceData.memberClientId as string;
      } else {
        const matchingMemberClient = memberClients.find(
          memberClient =>
            (data.client.company || "") === (memberClient.company || "") &&
            (data.client.contact || "") === (memberClient.name || "") &&
            data.client.contactEmail === memberClient.emailTo
        );

        if (matchingMemberClient) {
          memberClientId = matchingMemberClient.memberClientId;

          const additionalEmails = (data.client.contactEmailsCC || [])
            .map(i => i.email)
            .filter(e => !!e);

          if (!isEqual(additionalEmails, matchingMemberClient.emailCC || [])) {
            // Update memberClient if email CC was updated
            await paymentsService.memberClient.update(memberClientId, {
              emailCC: additionalEmails
            });
          }
        } else {
          const createdMemberClient = await paymentsService.memberClient.create(
            {
              memberId: userId,
              company: data.client.company || "",
              name:
                data.client.contact ||
                `${userQuery.data?.profile.firstName} ${userQuery.data?.profile.lastName}`.trim(),
              emailTo: data.client.contactEmail || userQuery.data?.email || "",
              emailCC: (data.client.contactEmailsCC || []).map(v => v.email),
              ...(data.client.qboCustomerId
                ? {
                    integration: {
                      quickbooks: {
                        itemId: data.client.qboItemId,
                        customerId: data.client.qboCustomerId
                      }
                    }
                  }
                : {})
            }
          );
          memberClientId = createdMemberClient.memberClientId;
          queryCache.invalidateQueries(QUERY_MEMBER_CLIENTS);
        }
      }

      // Handle collaborators
      const collaboratorsRequest = await createCollaboratorsRequest(
        data.collaborators,
        collaborators,
        userId,
        paymentsStatusQuery.data?.defaultAccountCurrency
      );

      const collaboratorsCreateRequestData = collaboratorsRequest.filter(
        collaborator => !!collaborator
      ) as IInvoiceCollaboratorCreateRequest[];

      const sendDate = generateSendDate({
        send: data.send
      });

      // Generate due date based on form values and send date
      const dueDate = generateDueDate({
        formValues: {
          due: data.due,
          customDueDate: data.customDueDate
        },
        sendDate
      });

      // Generate object with common invoice data which can be used for both invoice and invoiceTemplate
      const commonData = {
        memberId: userId,
        currency:
          paymentsStatusQuery.data?.defaultAccountCurrency || Currency.USD,
        memberClientId,
        invoiceNotes: data.other.notes,
        creditFeeHandling: {
          memberPays: data.advanced.clientPaysProcessingFees ? 0 : 100,
          clientPays: data.advanced.clientPaysProcessingFees ? 100 : 0
        },
        notificationPreferences: {
          sendInvoice: data.advanced.sendEmails,
          sendReminders: data.advanced.sendEmails && data.advanced.sendReminders
        },
        dueDate,
        lateFeeHandling: data.includesLateFee
          ? generateLateFeeConfig({
              formValues: data.lateFee,
              dueDate
            })
          : undefined,
        attachments: {
          customAttachmentIds: attachmentsFormDataToCreateRequest(
            data.other.attachments
          )
        },
        acceptedPaymentMethods: paymentMethodsFormDataToCreateRequest(
          data.paymentMethods
        ),
        labels: {
          projectName: data.other.projectName || ((undefined as any) as string),
          subject: data.subject
        },
        metadata: {
          purchaseOrderNumber: data.purchaseOrderNumber
        }
      };

      if (
        !data.recurring &&
        (data.send.type === "immediately" || isToday(sendDate))
      ) {
        // If invoice is not repeats and have to be sent immediately – we can work just with invoice entity

        if (invoiceId) {
          // If invoice is being edited – update invoice
          const isResubmit =
            (invoice?.events.clientDeclinedAt &&
              !invoice?.events.memberResubmittedAt) ||
            (invoice?.events.clientDeclinedAt &&
              invoice?.events.memberResubmittedAt &&
              invoice?.events.clientDeclinedAt >
                invoice?.events.memberResubmittedAt);

          savedInvoice = await paymentsService.invoice.update(invoiceId, {
            ...getChangedData(
              invoice,
              {
                ...commonData,
                collaborators: collaboratorsRequest,
                lineItems: convertToLineItemsUpdateRequest(data.lineItems),
                lateFeeHandling:
                  invoice?.lateFeeHandling && !commonData.lateFeeHandling
                    ? {
                        lateFeePercentage: null,
                        lateFeeAmount: null,
                        frequency: null
                      }
                    : commonData.lateFeeHandling,
                acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                  data.paymentMethods
                ),
                attachments: {
                  customAttachmentIds: attachmentsFormDataToUpdateRequest(
                    data.other.attachments,
                    invoice?.attachments?.customAttachmentIds || []
                  )
                }
              },
              { dismissTimeInDates: true }
            ),
            member: isResubmit
              ? {
                  workflowStatus: MemberWorkFlowStatus.Resubmitted
                }
              : undefined
          });
        } else if (invoiceTemplateId) {
          // This is the case when member have one-time scheduled for future invoice but wants to send it now, so they updates send date to today in Edit form. In this case we're just updating existing invoiceTemplate and it will create new Open invoice.

          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            invoiceTemplateId,
            getChangedData(
              invoiceTemplate,
              {
                invoiceData: {
                  ...commonData,
                  collaborators: collaboratorsRequest,
                  lineItems: convertToLineItemsUpdateRequest(data.lineItems),
                  acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                    data.paymentMethods
                  ),
                  attachments: {
                    customAttachmentIds: attachmentsFormDataToUpdateRequest(
                      data.other.attachments,
                      invoiceTemplate?.invoiceData.attachments
                        ?.customAttachmentIds || []
                    )
                  }
                },
                scheduleDates: [
                  {
                    date: sendDate
                  }
                ],
                labels: commonData.labels
              },
              { dismissTimeInDates: true }
            )
          );
        } else {
          // Create new invoice
          savedInvoice = await paymentsService.invoice.create({
            ...commonData,
            lineItems: convertToLineItemsCreateRequest(data.lineItems),
            collaborators: collaboratorsCreateRequestData,
            status: InvoiceStatus.Draft
          });
        }
      } else {
        // If invoice repeats OR sending should be delayed – we are working with invoiceTemplate entity

        // Generate frequency config if invoice have to be repeated
        const frequency = data.recurring
          ? generateFrequencyConfig({
              formValues: data.frequency,
              sendDate
            })
          : undefined;

        if (invoiceTemplateId) {
          let scheduleDates: (IScheduleDateUpdate | null)[] | undefined;

          if (invoiceTemplate?.isSchedulingOnly) {
            // New scheduling logic

            if (frequency) {
              // If invoice is repeatable

              if (frequency.endDate) {
                // If repeatable invoice has end date – we need to cut off everything after end date

                scheduleDates = invoiceTemplate?.scheduleDates?.map(
                  scheduleDate =>
                    frequency.endDate && scheduleDate.date > frequency.endDate
                      ? null
                      : {}
                );
              }

              // If repeatable invoice is endless don't do anything
            } else {
              // If it's one time scheduled invoice, just update send date

              scheduleDates = [
                {
                  date: sendDate
                }
              ];
            }
          } else {
            // Old scheduling logic

            scheduleDates = [
              {
                date: sendDate
              }
            ];
          }

          // If invoiceTemplate is being edited – update invoiceTemplate
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            invoiceTemplateId,
            getChangedData(
              invoiceTemplate,
              {
                invoiceData: {
                  ...commonData,
                  collaborators: collaboratorsRequest,
                  lineItems: convertToLineItemsUpdateRequest2(
                    invoiceTemplate?.invoiceData.lineItems || [],
                    data.lineItems
                  ),
                  lateFeeHandling:
                    invoiceTemplate?.invoiceData.lateFeeHandling &&
                    !commonData.lateFeeHandling
                      ? {
                          lateFeePercentage: null,
                          lateFeeAmount: null,
                          frequency: null
                        }
                      : commonData.lateFeeHandling,
                  acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                    data.paymentMethods
                  ),
                  attachments: {
                    customAttachmentIds: attachmentsFormDataToUpdateRequest(
                      data.other.attachments,
                      invoiceTemplate?.invoiceData.attachments
                        ?.customAttachmentIds || []
                    )
                  }
                },
                frequency: frequency
                  ? frequency
                  : invoiceTemplate?.frequency
                  ? null
                  : undefined,
                scheduleDates,
                labels: commonData.labels
              },
              { dismissTimeInDates: true }
            )
          );
        } else {
          if (invoiceId) {
            if (invoice?.status === InvoiceStatus.Draft) {
              await paymentsService.invoice.delete(invoiceId);
            }
          }

          // Create new invoiceTemplate
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.create({
            invoiceData: {
              ...commonData,
              collaborators: collaboratorsCreateRequestData,
              lineItems: convertToLineItemsCreateRequest(data.lineItems)
            },
            frequency,
            scheduleDates: [{ date: sendDate, status: ScheduleStatus.Pending }],
            status: FrequencyAndScheduleStatus.Draft,
            labels: commonData.labels,
            isSchedulingOnly: true
          });
        }
      }

      if (send) {
        await sendInvoice({
          invoiceId: savedInvoice ? savedInvoice.invoiceId : undefined,
          invoiceTemplateId: savedInvoiceTemplate
            ? savedInvoiceTemplate.invoiceTemplateId
            : undefined
        });
      }

      return {
        savedInvoice,
        savedInvoiceTemplate
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ savedInvoice, savedInvoiceTemplate }, { send }) => {
        if (!send) {
          queryCache.invalidateQueries(QUERY_MEMBER_CLIENTS);
          queryCache.invalidateQueries(QUERY_INVOICES);
          queryCache.invalidateQueries(QUERY_INVOICE_TEMPLATES);
          if (savedInvoice) {
            queryCache.invalidateQueries([
              QUERY_INVOICE,
              savedInvoice.invoiceId
            ]);
          }
          if (savedInvoiceTemplate) {
            queryCache.invalidateQueries([
              QUERY_INVOICE_TEMPLATE,
              savedInvoiceTemplate.invoiceTemplateId
            ]);
          }
          goBack();
        }
      }
    }
  );
};

export const useSendInvoice = () => {
  const history = useHistory();
  const location = useLocation();

  return useWSMutation<
    {
      invoice?: IInvoice;
      invoiceTemplate?: IInvoiceTemplate;
    },
    AxiosError,
    {
      invoiceId?: string;
      invoiceTemplateId?: string;
    }
  >(
    async ({ invoiceId, invoiceTemplateId }) => {
      let invoice: IInvoice | undefined;
      let invoiceTemplate: IInvoiceTemplate | undefined;

      if (invoiceId) {
        invoice = await paymentsService.invoice.update(invoiceId, {
          status: InvoiceStatus.Open
        });

        history.push(
          `/member/invoices/${invoiceId}/send-success`,
          location.state
        );
      }

      if (invoiceTemplateId) {
        invoiceTemplate = await paymentsService.invoiceTemplate.update(
          invoiceTemplateId,
          {
            status: FrequencyAndScheduleStatus.Active
          }
        );

        const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

        if (createdInvoice) {
          history.push(
            `/member/invoices/${createdInvoice.invoiceId}/send-success`,
            location.state
          );
        } else {
          history.push(
            `/member/invoices/template/${invoiceTemplateId}/send-success`,
            location.state
          );
        }
      }

      return {
        invoice,
        invoiceTemplate
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ invoice, invoiceTemplate }) => {
        queryCache.invalidateQueries(QUERY_INVOICES);

        if (invoice) {
          queryCache.invalidateQueries([QUERY_INVOICE, invoice.invoiceId]);
        }

        if (invoiceTemplate) {
          queryCache.invalidateQueries([
            QUERY_INVOICE_TEMPLATE,
            invoiceTemplate.invoiceTemplateId
          ]);
          queryCache.invalidateQueries(QUERY_INVOICE_TEMPLATES);
        }
      }
    }
  );
};

export const useCreateClientInvoice = () => {
  return useWSMutation<IClientInvoice, AxiosError, IClientInvoiceCreateRequest>(
    data => paymentsService.client.invoice.create(data),
    {
      throwOnError: false,
      dependencies: [QUERY_CLIENT_INVOICE]
    }
  );
};

export const useUpdateClientInvoice = (invoiceId: string) => {
  return useWSMutation<IClientInvoice, AxiosError, IClientInvoiceUpdateRequest>(
    data => paymentsService.client.invoice.update(invoiceId, data),
    {
      throwOnError: false,
      dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]]
    }
  );
};

export const usePayClientInvoice = (
  invoiceId: string,
  config?: ReactQueryMutationsConfig<IClientInvoice, AxiosError, IPayRequest>
) => {
  return useWSMutation<IClientInvoice, AxiosError, IPayRequest>(
    data => paymentsService.client.invoice.pay(invoiceId, data),
    {
      throwOnError: false,
      dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]],
      ...config
    }
  );
};

export const useUpdateClientInvoiceTemplate = () => {
  return useWSMutation<
    IClientInvoiceTemplate,
    AxiosError,
    { invoiceTemplateId: string; data: IClientInvoiceTemplateUpdateRequest }
  >(
    ({ invoiceTemplateId, data }) =>
      paymentsService.client.invoiceTemplate.update(invoiceTemplateId, data),
    {
      throwOnError: false,
      dependencies: [QUERY_CLIENT_INVOICE_TEMPLATE]
    }
  );
};

export const useCreateMemberClient = () =>
  useWSMutation<IMemberClient, AxiosError, IMemberClientCreateRequest>(
    paymentsService.memberClient.create,
    {
      throwOnError: false,
      dependencies: [QUERY_MEMBER_CLIENTS]
    }
  );

export const useUpdateMemberClient = (memberClientId: string) =>
  useWSMutation<IMemberClient, AxiosError, IMemberClientUpdateRequest>(
    data => paymentsService.memberClient.update(memberClientId, data),
    {
      throwOnError: false,
      dependencies: [
        QUERY_MEMBER_CLIENTS,
        [QUERY_MEMBER_CLIENT, memberClientId]
      ]
    }
  );

export const useCreateCollaborator = () =>
  useWSMutation<ICollaboratorSchema, AxiosError, ICollaboratorCreateRequest>(
    paymentsService.collaborator.create,
    {
      throwOnError: false,
      dependencies: [QUERY_COLLABORATORS]
    }
  );

export const useUpdateCollaborator = (collaboratorId: string) =>
  useWSMutation<ICollaboratorSchema, AxiosError, ICollaboratorUpdateRequest>(
    data => paymentsService.collaborator.update(collaboratorId, data),
    {
      throwOnError: false,
      dependencies: [QUERY_COLLABORATORS, [QUERY_COLLABORATOR, collaboratorId]]
    }
  );

export const useDynamicUpdateCollaborator = () =>
  useWSMutation<
    ICollaboratorSchema,
    AxiosError,
    ICollaboratorUpdateRequest & { collaboratorId: string }
  >(
    ({ collaboratorId, ...data }) =>
      paymentsService.collaborator.update(collaboratorId, data),
    {
      throwOnError: false,
      onSuccess: async collaborator => {
        queryCache.invalidateQueries(QUERY_COLLABORATORS);
        queryCache.invalidateQueries([
          QUERY_COLLABORATOR,
          collaborator.clientId
        ]);
        queryCache.invalidateQueries([
          QUERY_COLLABORATOR,
          collaborator.collaboratorId
        ]);
      }
    }
  );

export const useDynamicUpdateMemberClient = () =>
  useWSMutation<
    IMemberClient,
    AxiosError,
    { id: string } & IMemberClientUpdateRequest
  >(({ id, ...member }) => paymentsService.memberClient.update(id, member), {
    throwOnError: false,
    onSuccess: async memberClient => {
      queryCache.invalidateQueries(QUERY_MEMBER_CLIENTS);
      queryCache.invalidateQueries([
        QUERY_MEMBER_CLIENT,
        memberClient.clientId
      ]);
    }
  });

export const useDeleteMemberClient = (memberClientId: string) =>
  useWSMutation<IMemberClient, AxiosError>(
    () => paymentsService.memberClient.delete(memberClientId),
    {
      throwOnError: false,
      dependencies: [QUERY_MEMBER_CLIENTS]
    }
  );

export const useSavePayable = () => {
  const userId = useUserId();
  const collaboratorsQuery = useCollaboratorsQuery();
  const paymentsStatusQuery = usePaymentsStatusQuery();

  return useWSMutation<
    {
      savedPayable: IPayableSchema;
      savedCollaborator?: ICollaboratorSchema;
    },
    AxiosError,
    {
      payableId?: string;
      data: PayableFormValues;
    }
  >(
    async ({ data, payableId }) => {
      let savedCollaborator: ICollaboratorSchema | undefined;
      let savedPayable: IPayableSchema;

      const commonData = {
        currency:
          paymentsStatusQuery.data?.defaultAccountCurrency || Currency.USD,
        invoiceNotes: data.other.notes,
        labels: data.other.projectName
          ? {
              projectName: data.other.projectName
            }
          : undefined,
        attachments: {
          customAttachmentIds: attachmentsFormDataToCreateRequest(
            data.other.attachments
          )
        },
        client: {
          payDate: data.payDate
        },
        creditFeeHandling: {
          memberPays: 0,
          clientPays: 100
        },
        metadata: {
          purchaseOrderNumber: data.purchaseOrderNumber
        }
      };

      if (payableId) {
        const payable = await paymentsService.payable.get(payableId);

        savedPayable = await paymentsService.payable.update(
          payableId,
          getChangedData(
            payable,
            {
              ...commonData,
              lineItems:
                data.lineItems && data.lineItems.length > 0
                  ? convertToLineItemsUpdateRequest(data.lineItems)
                  : [
                      {
                        description: "",
                        totalCost: 0
                      }
                    ],
              attachments: {
                customAttachmentIds: attachmentsFormDataToUpdateRequest(
                  data.other.attachments,
                  payable.attachments?.customAttachmentIds || []
                )
              }
            },
            { dismissTimeInDates: true }
          )
        );
      } else {
        const matchingCollaborator = getVisibleCollaborators(
          collaboratorsQuery.data || []
        ).find(
          memberClient =>
            memberClient.member.user.email === data.collaboratorEmail
        );

        if (matchingCollaborator) {
          savedCollaborator = matchingCollaborator;
        } else {
          savedCollaborator = await paymentsService.collaborator.create({
            clientId: userId,
            memberEmail: data.collaboratorEmail,
            labels: data?.labels,
            ...(data?.collaboratorQBOVendor
              ? {
                  integration: {
                    quickbooks: {
                      vendorId: data.collaboratorQBOVendor,
                      expenseAccountId: data.collaboratorQBOExpenseAccount
                    }
                  }
                }
              : {})
          });
        }

        savedPayable = await paymentsService.payable.create({
          ...commonData,
          collaboratorId: savedCollaborator.collaboratorId,
          lineItems:
            data.lineItems && data.lineItems.length > 0
              ? convertToLineItemsCreateRequest(data.lineItems)
              : [
                  {
                    description: "",
                    totalCost: 0
                  }
                ],
          dueDate: data.payDate
        });
      }

      return {
        savedCollaborator,
        savedPayable
      };
    },
    {
      throwOnError: false,
      dependencies: [QUERY_MEMBER_CLIENTS, QUERY_INVOICES]
    }
  );
};

export const useSendPayable = () => {
  return useWSMutation<
    {
      payable?: IPayableSchema;
    },
    AxiosError,
    {
      action: SendPayableInvoiceAction;
      payableId: string;
    }
  >(
    async ({ action, payableId }) => {
      let payable: IPayableSchema;

      if (action === "pay") {
        payable = await paymentsService.payable.update(payableId, {
          status: InvoiceStatus.Open
        });

        if (payable.attachments?.invoiceLink) {
          openInNewTab(payable.attachments?.invoiceLink);
        } else {
        }
      } else if (action === "approve") {
        payable = await paymentsService.payable.update(payableId, {
          status: InvoiceStatus.Open,
          client: {
            workflowStatus: ClientWorkFlowStatus.Approved
          }
        });
      } else {
        payable = await paymentsService.payable.update(payableId, {
          status: InvoiceStatus.Open
        });
      }

      return {
        payable
      };
    },
    {
      throwOnError: false,
      dependencies: [QUERY_INVOICES],
      onSuccess: ({ payable }) => {
        if (payable) {
          queryCache.invalidateQueries([QUERY_PAYABLE, payable?.payableId]);
        }
      }
    }
  );
};

export const useApprovePayable = () => {
  return useWSMutation<
    {
      payable: IPayableSchema;
    },
    AxiosError
  >(
    async (payableId: string) => {
      const payable = await paymentsService.payable.update(payableId, {
        client: {
          workflowStatus: ClientWorkFlowStatus.Approved
        }
      });

      return {
        payable
      };
    },
    {
      onSuccess: ({ payable }) => {
        queryCache.refetchQueries(QUERY_PAYABLES);
        queryCache.refetchQueries([QUERY_PAYABLE, payable.payableId]);
      },
      throwOnError: false
    }
  );
};

export const usePreApprovePayable = () => {
  return useWSMutation<
    {
      payable: IPayableSchema;
    },
    AxiosError
  >(
    async (payableId: string) => {
      const payable = await paymentsService.payable.update(payableId, {
        client: {
          workflowStatus: ClientWorkFlowStatus.PreApproved
        }
      });

      return {
        payable
      };
    },
    {
      onSuccess: ({ payable }) => {
        queryCache.refetchQueries(QUERY_PAYABLES);
        queryCache.refetchQueries([QUERY_PAYABLE, payable.payableId]);
      },
      throwOnError: false
    }
  );
};

export const useUpdatePayrollSettings = (
  memberId: string,
  config?: QueryConfig<IPayrollSettings, AxiosError>
) => {
  return useWSMutation<IPayrollSettings, AxiosError, IPayrollSettingsUpdate>(
    data => paymentsService.payrollSettings.update(memberId, data),
    {
      dependencies: [QUERY_PAYROLL_SETTINGS],
      ...config
    }
  );
};

export const useUnapprovePayable = () => {
  return useWSMutation<
    {
      payable: IPayableSchema;
    },
    AxiosError
  >(
    async (payableId: string) => {
      const payable = await paymentsService.payable.update(payableId, {
        client: {
          workflowStatus: ClientWorkFlowStatus.Pending
        }
      });

      return {
        payable
      };
    },
    {
      onSuccess: ({ payable }) => {
        queryCache.refetchQueries(QUERY_PAYABLES);
        queryCache.refetchQueries([QUERY_PAYABLE, payable.payableId]);
      },
      throwOnError: false
    }
  );
};

export const useSendCollaboratorInvite = () => {
  const { track } = useWSAnalytics();
  const userId = useUserId();
  const userQuery = useUserProfile(userId);
  const memberQuery = useMemberProfile(userId);
  const collaboratorsQuery = useCollaboratorsQuery();

  return useWSMutation(
    async (collaboratorId: string) => {
      const collaborator = (collaboratorsQuery.data || []).find(
        c => c.collaboratorId === collaboratorId
      );
      const user = userQuery.data;
      const member = memberQuery.data;

      if (collaborator && user && member) {
        track.other("Invite Reminder Sent", {
          userId: collaborator.memberId,
          inviter_email: user.email,
          inviter_first_name: user.profile?.firstName,
          inviter_last_name: user.profile?.lastName,
          inviter_company:
            member.profile?.company?.name ||
            `${user.profile?.firstName} ${user.profile?.lastName}`,
          inviter_growth_name: user.labels?.growth_name,
          type:
            user.settings?.userAccountType === UserAccountType.enterprise
              ? "Enterprise"
              : "Collaborator",
          context: {
            groupId: user.userId
          }
        });
      }
    },
    {
      throwOnError: false
    }
  );
};

export const useCancelPayable = () => {
  return useWSMutation<
    IPayableSchema,
    AxiosError,
    {
      payableId: string;
    }
  >(
    async ({ payableId }) => {
      return await paymentsService.payable.update(payableId, {
        status: InvoiceStatus.Cancelled
      });
    },
    {
      dependencies: [QUERY_PAYABLES]
    }
  );
};

export const useDeletePayable = () => {
  return useWSMutation<
    IPayableSchema,
    AxiosError,
    {
      payableId: string;
    }
  >(
    async ({ payableId }) => {
      return await paymentsService.payable.delete(payableId);
    },
    {
      dependencies: [QUERY_PAYABLES]
    }
  );
};

export const useRejectPayable = () => {
  return useWSMutation<
    IPayableSchema,
    AxiosError,
    {
      payableId: string;
      comment: string;
    }
  >(
    async ({ payableId, comment }) => {
      return await paymentsService.payable.update(payableId, {
        client: {
          comment,
          workflowStatus: ClientWorkFlowStatus.Declined
        }
      });
    },
    {
      dependencies: [QUERY_PAYABLES]
    }
  );
};

export const useDownloadInvoicePdf = () => {
  const downloadPdf = useDownloadPdf();

  return useWSMutation<
    IInvoice,
    AxiosError,
    {
      invoiceId: string;
    }
  >(async ({ invoiceId }) => {
    const invoice = await paymentsService.invoice.generatePdf(invoiceId);

    if (invoice.attachments?.receiptPdf) {
      downloadPdf(invoice.attachments.receiptPdf);
    } else if (invoice.attachments?.invoicePdf) {
      downloadPdf(invoice.attachments.invoicePdf);
    }

    return invoice;
  });
};

export const useDownloadCollaborator1099 = () => {
  const downloadPdf = useDownloadPdf();

  return useWSMutation<
    void,
    AxiosError,
    {
      collaboratorId: string;
      collaborator: ICollaboratorSchema;
    }
  >(async ({ collaboratorId, collaborator }) => {
    const blob = await paymentsService.collaborator.download1099(
      collaboratorId
    );
    const url = window.URL.createObjectURL(blob);
    downloadPdf(url, `${collaborator.member.user.email}-2021-form1099.pdf`);
  });
};

export const useUpdatePaymentsService = () => {
  return useWSMutation(
    async ({
      userId,
      ...request
    }: IServiceUpdateRequest & { userId: string }) => {
      return await paymentsService.service.update(userId, request);
    }
  );
};

export const useCreateCard = () => {
  return useWSMutation(
    async (data: ICardCreateRequest) => {
      return await paymentsService.banking.card.create(data);
    },
    {
      dependencies: [QUERY_CARD_LIST, QUERY_BOOKKEEPING_BANKING_ACCOUNT]
    }
  );
};

export const useReplaceCard = () => {
  return useWSMutation(
    async (payload: { id: string; data: ICardCreateRequest }) => {
      return await paymentsService.banking.card.replace(payload);
    },
    {
      dependencies: [QUERY_CARD_LIST, QUERY_BOOKKEEPING_BANKING_ACCOUNT]
    }
  );
};

export const useUpdateCard = () => {
  return useWSMutation(
    async (payload: { id: string; data: ICardUpdateRequest }) => {
      return await paymentsService.banking.card.update(
        payload.id,
        payload.data
      );
    },
    {
      dependencies: [QUERY_CARD_LIST]
    }
  );
};

export const useDeleteCard = () => {
  return useWSMutation(
    async (id: string) => {
      return await paymentsService.banking.card.delete(id);
    },
    {
      dependencies: [QUERY_CARD_LIST]
    }
  );
};

export const useCreateCardToken = () => {
  return useWSMutation(
    async ({ id, ...data }: ICardCodeRequest & { id: string }) => {
      return await paymentsService.banking.card.token.create(id, data);
    }
  );
};

export const useUpdateCardToken = () => {
  return useWSMutation(
    async ({ id, ...data }: ICardTokenRequest & { id: string }) => {
      return await paymentsService.banking.card.token.update(id, data);
    }
  );
};

export const useRequestCardVerificationToken = () =>
  useWSMutation(({ id, ...data }: ICardCodeRequest & { id: string }) =>
    paymentsService.banking.card.token.create(id, data)
  );

export const useRequestCardToken = () =>
  useWSMutation(({ id, ...data }: ICardTokenRequest & { id: string }) =>
    paymentsService.banking.card.token.update(id, data)
  );

export const useLockCard = () =>
  useWSMutation(
    (id: string) =>
      paymentsService.banking.card.update(id, {
        status: CardStatusUpdate.Frozen
      }),
    {
      onSuccess: (_, id) => {
        queryCache.invalidateQueries([QUERY_CARD, id]);
        queryCache.invalidateQueries(QUERY_CARD_LIST);
      }
    }
  );

export const useUnlockCard = () =>
  useWSMutation(
    (id: string) =>
      paymentsService.banking.card.update(id, {
        status: CardStatusUpdate.Active
      }),
    {
      onSuccess: (_, id) => {
        queryCache.invalidateQueries([QUERY_CARD, id]);
        queryCache.invalidateQueries(QUERY_CARD_LIST);
      }
    }
  );

export const useTemrinateCard = () =>
  useWSMutation(
    (id: string) =>
      paymentsService.banking.card.update(id, {
        status: CardStatusUpdate.ClosedByCustomer
      }),
    {
      onSuccess: (_, id) => {
        queryCache.invalidateQueries(QUERY_CARD_LIST);
      }
    }
  );

export const useSetStolenCard = () =>
  useWSMutation(
    (id: string) =>
      paymentsService.banking.card.update(id, {
        status: CardStatusUpdate.Stolen
      }),
    {
      onSuccess: (_, id) => {
        queryCache.invalidateQueries(QUERY_CARD_LIST);
      }
    }
  );

export const useSetLostCard = () =>
  useWSMutation(
    (id: string) =>
      paymentsService.banking.card.update(id, {
        status: CardStatusUpdate.ClosedByCustomer
      }),
    {
      onSuccess: (_, id) => {
        queryCache.invalidateQueries(QUERY_CARD_LIST);
      }
    }
  );

export const useCreateCollaboratorsGroup = () =>
  useWSMutation(
    (requestData: ICollaboratorGroupCreateRequest) =>
      paymentsService.collaboratorGroup.create(requestData),
    {
      dependencies: [QUERY_COLLABORATOR_GROUPS, QUERY_ALL_COLLABORATOR_GROUPS]
    }
  );

export const useUpdateCollaboratorsGroup = () =>
  useWSMutation(
    (requestData: { id: string } & ICollaboratorGroupUpdateRequest) =>
      paymentsService.collaboratorGroup.update(requestData.id, requestData),
    {
      dependencies: [QUERY_COLLABORATOR_GROUPS, QUERY_ALL_COLLABORATOR_GROUPS],
      onSuccess: (_, { id }) => {
        queryCache.invalidateQueries([QUERY_COLLABORATOR_GROUP, id]);
      }
    }
  );

export const useDeleteCollaboratorsGroup = () =>
  useWSMutation((id: string) => paymentsService.collaboratorGroup.delete(id), {
    dependencies: [QUERY_COLLABORATOR_GROUPS, QUERY_ALL_COLLABORATOR_GROUPS]
  });

export const useCreateCollaboratorSettingsAdditionalData = () =>
  useWSMutation(
    (payload: IAdditionalData) =>
      paymentsService.collaboratorSettings.create(payload),
    {
      dependencies: [QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_DATA]
    }
  );

export const useUpdateCollaboratorSettingsAdditionalData = () =>
  useWSMutation(
    (payload: { id: string; data: IAdditionalData }) =>
      paymentsService.collaboratorSettings.update(payload),
    {
      dependencies: [
        QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_DATA,
        QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_ITEM_DATA
      ]
    }
  );

export const useDeleteCollaboratorSettingsAdditionalData = () =>
  useWSMutation(
    (id: string) => paymentsService.collaboratorSettings.delete(id),
    {
      dependencies: [QUERY_COLLABORATOR_SETTINGS_ADDITIONAL_DATA]
    }
  );

export const useCollaboratorDownloadW9 = () => {
  const downloadPdf = useDownloadPdf();

  return useWSMutation<
    void,
    AxiosError,
    {
      collaboratorId: string;
    }
  >(async ({ collaboratorId }) => {
    const collaborator = await paymentsService.collaborator.get(collaboratorId);
    const blob = await paymentsService.collaborator.downloadW9(collaboratorId);
    const url = window.URL.createObjectURL(blob);
    downloadPdf(url, `${collaborator.member.user.email}-w9-form.pdf`);
  });
};

export const useCreateClientDeduction = () =>
  useWSMutation(
    (data: IDeductionCreateRequest) =>
      paymentsService.clientDeductions.create(data),
    {
      dependencies: [QUERY_CLIENT_DEDUCTIONS]
    }
  );

export const useUpdateClientDeduction = () =>
  useWSMutation(
    (payload: { id: string; data: IDeductionUpdateRequest }) =>
      paymentsService.clientDeductions.update(payload.id, payload.data),
    {
      dependencies: [QUERY_CLIENT_DEDUCTIONS, QUERY_CLIENT_DEDUCTION]
    }
  );

export const useDeleteClientDeduction = () =>
  useWSMutation((id: string) => paymentsService.clientDeductions.delete(id), {
    dependencies: [QUERY_CLIENT_DEDUCTIONS]
  });

export const useCreateCollaboratorDeduction = () =>
  useWSMutation(
    (data: IDeductionCreateRequest) =>
      paymentsService.collaboratorDeductions.create(data),
    {
      dependencies: [QUERY_COLLABORATOR_DEDUCTIONS]
    }
  );

export const useUpdateCollaboratorDeduction = () =>
  useWSMutation(
    (payload: { id: string; data: IDeductionUpdateRequest }) =>
      paymentsService.collaboratorDeductions.update(payload.id, payload.data),
    {
      dependencies: [
        QUERY_COLLABORATOR_DEDUCTIONS,
        QUERY_COLLABORATOR_DEDUCTION
      ]
    }
  );

export const useDeleteCollaboratorDeduction = () =>
  useWSMutation(
    (id: string) => paymentsService.collaboratorDeductions.delete(id),
    {
      dependencies: [QUERY_COLLABORATOR_DEDUCTIONS]
    }
  );

export const useCreateBulkPayableBatch = () =>
  useWSMutation((data: IBulkPayableBatchCreate) =>
    paymentsService.bulkPayable.batch.create(data)
  );

export const useUpdateBulkPayableBatch = () =>
  useWSMutation(
    (payload: { id: string; data: IBulkPayableBatchUpdate }) =>
      paymentsService.bulkPayable.batch.update(payload.id, payload.data),
    {
      onSuccess: response => {
        queryCache.invalidateQueries([
          QUERY_BULK_PAYABLE_BATCH,
          response.bulkPayableBatchId
        ]);
      }
    }
  );

export const useCreateBulkCollaboratorBatch = () =>
  useWSMutation((data: IBulkBatchCreate) =>
    paymentsService.bulkCollaborator.batch.create(data)
  );

export const useUpdateBulkCollaboratorBatch = () =>
  useWSMutation(
    (payload: { id: string; data: IBulkBatchUpdate }) =>
      paymentsService.bulkCollaborator.batch.update(payload.id, payload.data),
    {
      onSuccess: response => {
        queryCache.invalidateQueries([
          QUERY_BULK_COLLABORATOR_BATCH,
          response.bulkCollaboratorBatchId
        ]);
      }
    }
  );

export const useCreateBulkCalculate1099Batch = (
  config?: QueryConfig<IBulkCalculation1099Batch, AxiosError>
) =>
  useWSMutation(
    (data: IBulkBatchCreate) =>
      paymentsService.collaborator.nec1099.bulkCalculation1099Batch.create(
        data
      ),
    {
      ...config
    }
  );

export const useUpdateBulkCalculate1099Batch = () =>
  useWSMutation(
    (payload: { id: string; data: IBulkBatchUpdate }) =>
      paymentsService.collaborator.nec1099.bulkCalculation1099Batch.update(
        payload.id,
        payload.data
      ),
    {
      onSuccess: response => {
        queryCache.invalidateQueries([
          QUERY_BULK_CALCULATE_1099_BATCH,
          response.bulkCalculation1099BatchId
        ]);
      }
    }
  );

export const useCalculate1099NECTotal = () =>
  useWSMutation(
    (data: ICalculate1099Request) =>
      paymentsService.collaborator.nec1099.calculate1099NECTotal(data),
    {
      onSuccess: ({ collaboratorId }) => {
        queryCache.invalidateQueries([QUERY_COLLABORATOR, collaboratorId]);
      }
    }
  );
